import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { ChangePasswordPayload } from '@/types/api';

/**
 * Change your own password.
 *
 * `api`, not `clinicApi`: an account belongs to the person, not to the clinic
 * whose URL they happen to be standing in — the same password opens every
 * clinic they are a member of, and a user with no membership still has one.
 *
 * Nothing is invalidated on success. The server keeps the session valid (the
 * token names who you are, not what you know), so re-fetching would only churn
 * queries whose answers cannot have changed.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: ChangePasswordPayload) =>
      api.post<{ changed: true }>('/api/auth/me/password', payload),
  });
}

/** Which input a rejected change was about, when the server said. */
export type PasswordField = 'currentPassword' | 'newPassword';

/**
 * Read the field off a rejected change so the message lands under the input it
 * is about. Returns null for failures that are not about a specific field
 * (offline, 500), which the caller shows as a form-level error.
 */
export function passwordErrorField(err: unknown): PasswordField | null {
  if (!(err instanceof ApiError) || err.code !== 'VALIDATION_ERROR') return null;
  const field = (err.details as { field?: unknown } | null | undefined)?.field;
  return field === 'currentPassword' || field === 'newPassword' ? field : null;
}
